import { useCallback, useEffect, useRef, useState } from "react";
import { AlertCircle, Building2, Loader2, RefreshCw } from "lucide-react";
import {
  CanonicalReviewDecisionDialog,
  type CanonicalReviewDecision,
} from "@/components/compliance-review/CanonicalReviewDecisionDialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type {
  CanonicalCompanyComplianceDetail,
  CanonicalCompanyComplianceHistory,
  CanonicalCompanyComplianceQueue,
  CanonicalCompanyComplianceQueueItem,
  CanonicalCompanyCompliancePreview,
} from "@/data/contractor/compliance/canonicalCompanyComplianceReview";
import {
  CANONICAL_INTERNAL_REVIEW_READ_ERROR_MESSAGES,
  CanonicalInternalReviewReadClientError,
} from "@/services/canonical-internal-review-read.shared";
import {
  createCompanyComplianceOpsReviewReadClient,
} from "@/services/company-compliance-ops-review.service";

type CompanyReadClient = ReturnType<
  typeof createCompanyComplianceOpsReviewReadClient
>;

const defaultClient = createCompanyComplianceOpsReviewReadClient();
const REQUIREMENT_LABELS = Object.freeze({
  license: "License",
  insurance: "Insurance",
  w9: "W-9",
});

function safeDate(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function errorMessage(error: unknown) {
  const kind = error instanceof CanonicalInternalReviewReadClientError
    ? error.kind
    : "unexpected_error";
  return CANONICAL_INTERNAL_REVIEW_READ_ERROR_MESSAGES[kind];
}

function statusLabel(item: CanonicalCompanyComplianceQueueItem): string {
  return item.reviewState.replace(/_/g, " ");
}

export function CanonicalCompanyComplianceReadPanel({
  organizationId,
  contractorId,
  contractorLabel,
  contextKey,
  client = defaultClient,
}: Readonly<{
  organizationId: string;
  contractorId: string;
  contractorLabel: string;
  contextKey: string;
  client?: CompanyReadClient;
}>) {
  const generation = useRef(0);
  const previewGeneration = useRef(0);
  const [queue, setQueue] = useState<CanonicalCompanyComplianceQueue | null>(
    null,
  );
  const [queueLoading, setQueueLoading] = useState(false);
  const [queueError, setQueueError] = useState<unknown>(null);
  const [detail, setDetail] = useState<CanonicalCompanyComplianceDetail | null>(
    null,
  );
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<unknown>(null);
  const [history, setHistory] =
    useState<CanonicalCompanyComplianceHistory | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<unknown>(null);
  const [preview, setPreview] =
    useState<CanonicalCompanyCompliancePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<unknown>(null);
  const [decisionTarget, setDecisionTarget] = useState<Readonly<{
    item: CanonicalCompanyComplianceQueueItem;
    decision: CanonicalReviewDecision;
  }> | null>(null);
  const [successMessage, setSuccessMessage] = useState("");

  const loadQueue = useCallback(async () => {
    const requestGeneration = ++generation.current;
    setQueueLoading(true);
    setQueueError(null);
    try {
      const result = await client.listQueue({
        organizationId,
        contractorId,
        limit: 25,
      });
      if (generation.current !== requestGeneration) return;
      setQueue(result);
    } catch (error) {
      if (generation.current !== requestGeneration) return;
      setQueue(null);
      setQueueError(error);
    } finally {
      if (generation.current === requestGeneration) setQueueLoading(false);
    }
  }, [client, contractorId, organizationId]);

  useEffect(() => {
    generation.current += 1;
    setQueue(null);
    setQueueError(null);
    setDetail(null);
    setDetailLoading(false);
    setDetailError(null);
    setHistory(null);
    setHistoryLoading(false);
    setHistoryError(null);
    setPreview(null);
    setPreviewLoading(false);
    setPreviewError(null);
    setDecisionTarget(null);
    setSuccessMessage("");
    void loadQueue();
    return () => {
      generation.current += 1;
      previewGeneration.current += 1;
    };
  }, [contextKey, loadQueue]);

  useEffect(() => {
    if (!preview) return;
    const remaining = new Date(preview.expiresAt).getTime() - Date.now();
    if (remaining <= 0) {
      setPreview(null);
      return;
    }
    const timeout = window.setTimeout(() => setPreview(null), remaining);
    return () => window.clearTimeout(timeout);
  }, [preview]);

  async function openDetail(
    recordId: string,
    preserveDecisionDraft = false,
  ): Promise<void> {
    if (detailLoading) return;
    const requestGeneration = ++generation.current;
    previewGeneration.current += 1;
    setPreview(null);
    setPreviewError(null);
    if (!preserveDecisionDraft) setDecisionTarget(null);
    setDetailLoading(true);
    setDetailError(null);
    setHistory(null);
    setHistoryError(null);
    try {
      const result = await client.getDetail({
        organizationId,
        contractorId,
        recordId,
      });
      if (generation.current === requestGeneration) setDetail(result);
    } catch (error) {
      if (generation.current === requestGeneration) {
        setDetail(null);
        setDetailError(error);
      }
    } finally {
      if (generation.current === requestGeneration) setDetailLoading(false);
    }
  }

  async function openHistory(evidenceId: string): Promise<void> {
    if (historyLoading) return;
    const requestGeneration = ++generation.current;
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const result = await client.getHistory({
        organizationId,
        contractorId,
        evidenceId,
      });
      if (generation.current === requestGeneration) setHistory(result);
    } catch (error) {
      if (generation.current === requestGeneration) {
        setHistory(null);
        setHistoryError(error);
      }
    } finally {
      if (generation.current === requestGeneration) setHistoryLoading(false);
    }
  }

  async function openPreview(evidenceId: string): Promise<void> {
    if (previewLoading) return;
    const requestGeneration = ++previewGeneration.current;
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const result = await client.getPreview({
        organizationId,
        contractorId,
        evidenceId,
      });
      if (previewGeneration.current === requestGeneration) setPreview(result);
    } catch (error) {
      if (previewGeneration.current === requestGeneration) {
        setPreview(null);
        setPreviewError(error);
      }
    } finally {
      if (previewGeneration.current === requestGeneration) {
        setPreviewLoading(false);
      }
    }
  }

  function closePreview(): void {
    previewGeneration.current += 1;
    setPreview(null);
    setPreviewError(null);
    setPreviewLoading(false);
  }

  async function submitDecision(input: Readonly<{
    reasonCode?: string;
    note?: string;
    idempotencyKey: string;
  }>): Promise<void> {
    if (!decisionTarget) return;
    const { item, decision } = decisionTarget;
    try {
      await client.submitDecision({
        organizationId,
        contractorId,
        evidenceId: item.evidenceId!,
        decision,
        ...input,
        expectedLatestDecisionId: item.latestDecisionId,
      });
      setDecisionTarget(null);
      setSuccessMessage("Company evidence review decision recorded.");
      await loadQueue();
      await openDetail(item.recordId);
      await openHistory(item.evidenceId!);
    } catch (error) {
      if (
        error instanceof CanonicalInternalReviewReadClientError
        && (
          error.kind === "stale_decision"
          || error.kind === "idempotency_conflict"
        )
      ) {
        await loadQueue();
        await openDetail(item.recordId, true);
        await openHistory(item.evidenceId!);
      }
      throw error;
    }
  }

  const companyIdentityResolved = Boolean(contractorLabel.trim());
  const records = new Map<string, CanonicalCompanyComplianceQueueItem[]>();
  for (const item of queue?.items ?? []) {
    const items = records.get(item.recordId) ?? [];
    items.push(item);
    records.set(item.recordId, items);
  }

  return (
    <Card aria-label="Company Compliance review">
      <CardHeader className="space-y-2">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Building2 className="h-4 w-4" />
              Company Compliance
            </CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              Canonical company evidence review under {contractorLabel}.
              Company decisions remain separate from Individual Compliance and
              Relationship Review.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={queueLoading}
            onClick={() => void loadQueue()}
          >
            <RefreshCw className="mr-1 h-3.5 w-3.5" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {queueLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading company review queue…
          </div>
        ) : null}

        {queueError ? (
          <Alert variant="destructive" role="alert">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{errorMessage(queueError).title}</AlertTitle>
            <AlertDescription>
              {errorMessage(queueError).description}
            </AlertDescription>
          </Alert>
        ) : null}
        {successMessage ? (
          <Alert aria-live="polite">
            <AlertTitle>Decision recorded</AlertTitle>
            <AlertDescription>{successMessage}</AlertDescription>
          </Alert>
        ) : null}

        {!queueLoading && !queueError && queue?.items.length === 0 ? (
          <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
            No companies currently require canonical review. An empty company
            queue is valid for this selected parent.
          </div>
        ) : null}

        {[...records.entries()].map(([recordId, items]) => (
          <div key={recordId} className="rounded-md border p-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="font-medium">
                  {companyIdentityResolved
                    ? contractorLabel
                    : "Company identity unavailable"}
                </p>
                <p className="text-xs text-muted-foreground">
                  {items.length} canonical requirement
                  {items.length === 1 ? "" : "s"}
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={!companyIdentityResolved || detailLoading}
                onClick={() => void openDetail(recordId)}
              >
                Open company detail
              </Button>
            </div>
            <div className="mt-3 grid gap-2 md:grid-cols-3">
              {items.map((item) => (
                <div
                  key={item.requirementKey}
                  className="rounded border p-2 text-xs"
                >
                  <p className="font-medium">
                    {REQUIREMENT_LABELS[item.requirementKey]}
                  </p>
                  <p className="mt-1 text-muted-foreground">
                    {item.evidenceId
                      ? item.fileName ?? "Canonical evidence"
                      : "No canonical submission"}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    <Badge variant="outline">
                      {item.evidenceEligible ? "Evidence eligible" : "Evidence ineligible"}
                    </Badge>
                    <Badge variant="outline">{statusLabel(item)}</Badge>
                    <Badge variant="outline">
                      {item.operationallyVerified
                        ? "Operationally verified"
                        : "Not operationally verified"}
                    </Badge>
                  </div>
                  {item.failureReason ? (
                    <p className="mt-2 text-xs text-muted-foreground">
                      {item.failureReason.replace(/_/g, " ")}
                    </p>
                  ) : null}
                  {item.evidenceId ? (
                    <Button
                      className="mt-2"
                      size="sm"
                      variant="outline"
                      onClick={() => void openDetail(item.recordId)}
                    >
                      Review evidence
                    </Button>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ))}

        {detailLoading ? (
          <p className="text-sm text-muted-foreground">Loading company detail…</p>
        ) : null}
        {detailError ? (
          <Alert variant="destructive" role="alert">
            <AlertTitle>{errorMessage(detailError).title}</AlertTitle>
            <AlertDescription>
              {errorMessage(detailError).description}
            </AlertDescription>
          </Alert>
        ) : null}
        {detail ? (
          <div className="rounded-md border p-3" aria-label="Company review detail">
            <h3 className="font-semibold">{contractorLabel}</h3>
            <p className="text-xs text-muted-foreground">
              Canonical Company Compliance detail
            </p>
            <div className="mt-3 space-y-2">
              {detail.requirements.map((item) => (
                <div
                  key={item.requirementKey}
                  className="rounded border p-2 text-sm"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="font-medium">
                        {REQUIREMENT_LABELS[item.requirementKey]}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {item.evidenceId
                          ? item.fileName ?? "Canonical evidence"
                          : "No canonical submission"}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Submitted: {safeDate(item.submittedAt)} · Expires:{" "}
                        {safeDate(item.expiresAt)}
                      </p>
                      {item.failureReason ? (
                        <p className="text-xs text-muted-foreground">
                          Status detail: {item.failureReason.replace(/_/g, " ")}
                        </p>
                      ) : null}
                    </div>
                    {item.evidenceId ? (
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={previewLoading}
                          onClick={() => void openPreview(item.evidenceId!)}
                        >
                          Preview
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={historyLoading}
                          onClick={() => void openHistory(item.evidenceId!)}
                        >
                          View immutable history
                        </Button>
                      </div>
                    ) : null}
                  </div>
                  <p className="mt-2 text-xs">
                    Evidence: {item.evidenceEligible ? "Eligible" : "Not eligible"}
                    {" · "}Review: {statusLabel(item)}
                    {" · "}Operational:{" "}
                    {item.operationallyVerified ? "Verified" : "Not verified"}
                  </p>
                  {item.evidenceId ? (
                    <div className="mt-3 flex flex-wrap gap-2" aria-label={`${REQUIREMENT_LABELS[item.requirementKey]} review actions`}>
                      <Button
                        size="sm"
                        disabled={!item.evidenceEligible}
                        onClick={() => setDecisionTarget({
                          item,
                          decision: "approved",
                        })}
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={!item.evidenceEligible}
                        onClick={() => setDecisionTarget({
                          item,
                          decision: "rejected",
                        })}
                      >
                        Reject
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!item.evidenceEligible}
                        onClick={() => setDecisionTarget({
                          item,
                          decision: "changes_requested",
                        })}
                      >
                        Request Changes
                      </Button>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {historyError ? (
          <Alert variant="destructive" role="alert">
            <AlertTitle>{errorMessage(historyError).title}</AlertTitle>
            <AlertDescription>
              {errorMessage(historyError).description}
            </AlertDescription>
          </Alert>
        ) : null}
        {history ? (
          <div className="rounded-md border p-3" aria-label="Company decision history">
            <h3 className="font-semibold">Immutable decision history</h3>
            {history.decisions.length === 0 ? (
              <p className="mt-2 text-sm text-muted-foreground">
                No company review decisions recorded.
              </p>
            ) : (
              <div className="mt-2 space-y-2">
                {history.decisions.map((decision) => (
                  <div key={decision.decisionId} className="rounded border p-2 text-xs">
                    <p className="font-medium">
                      {decision.decision.replace(/_/g, " ")}
                    </p>
                    <p className="text-muted-foreground">
                      {safeDate(decision.decidedAt)} · {decision.reviewerRole}
                    </p>
                    {decision.reasonCode ? (
                      <p>Reason: {decision.reasonCode.replace(/_/g, " ")}</p>
                    ) : null}
                    {decision.note ? <p className="whitespace-pre-wrap">{decision.note}</p> : null}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : null}

        {previewError ? (
          <Alert variant="destructive" role="alert">
            <AlertTitle>{errorMessage(previewError).title}</AlertTitle>
            <AlertDescription>{errorMessage(previewError).description}</AlertDescription>
          </Alert>
        ) : null}
        <p className="text-xs text-muted-foreground">
          Company evidence decisions remain separate from Individual
          Compliance and Relationship Review decisions.
        </p>
      </CardContent>
      <Dialog open={Boolean(preview)} onOpenChange={(open) => !open && closePreview()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{preview?.fileName ?? "Company evidence preview"}</DialogTitle>
            <DialogDescription>
              This private preview is short-lived and does not approve the evidence.
            </DialogDescription>
          </DialogHeader>
          {preview ? (
            preview.mimeType === "application/pdf"
            || preview.mimeType.startsWith("image/")
          ) ? (
            preview.mimeType.startsWith("image/") ? (
              <img
                className="max-h-[70vh] w-full object-contain"
                src={preview.signedUrl}
                alt={preview.fileName}
              />
            ) : (
              <iframe
                className="h-[70vh] w-full rounded border"
                src={preview.signedUrl}
                title={preview.fileName}
              />
            )
          ) : (
            <p className="text-sm text-muted-foreground">
              This file type cannot be displayed inline.
            </p>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={closePreview}>
              Close preview
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <CanonicalReviewDecisionDialog
        decision={decisionTarget?.decision ?? null}
        targetLabel={decisionTarget
          ? `${contractorLabel} ${REQUIREMENT_LABELS[decisionTarget.item.requirementKey]}`
          : "selected company evidence"}
        onClose={() => setDecisionTarget(null)}
        onConfirm={submitDecision}
      />
    </Card>
  );
}
